import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductModal from "@/components/products/ProductModal";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Search, ArrowRight, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";

// Import product images
import sacKraftPoignees from "@/assets/products/sac-kraft-poignees.png";
import bolKraft from "@/assets/products/bol-kraft-500ml.jpg";
import gobeletKraft110 from "@/assets/products/gobelet-kraft-110ml.png";
import gobeletBlanc200 from "@/assets/products/gobelet-blanc-200ml.jpg";
import bobineEssuieMains from "@/assets/products/bobine-essuie-mains.png";
import serviettesBlanches from "@/assets/products/serviettes-blanches.png";
import filmAlimentaire from "@/assets/products/film-alimentaire.png";
import filmAlu from "@/assets/products/film-alu.png";
import bolPersonnalise from "@/assets/products/bol-personnalise.png";
import sacPersonnalise from "@/assets/products/sac-personnalise-ams-duo.png";
import gantsNitrileBleus from "@/assets/products/gants-nitrile-bleus.png";
import gantsNitrileBleusSeul from "@/assets/products/gants-nitrile-bleus-seul.png";
import gantsVinyle from "@/assets/products/gants-vinyle-seul.png";
import gantsNitrileNoirs from "@/assets/products/gants-nitrile-noirs-seul.png";
import saladierCarre500 from "@/assets/products/saladier-carre-500ml.jpg";
import pailleKraft from "@/assets/products/paille-kraft.jpg";
import barquetteBateauPresentation from "@/assets/products/barquette-bateau-presentation.png";
import potGlace90 from "@/assets/products/pot-glace-90ml.jpg";
import potSoupe8oz from "@/assets/products/pot-soupe-8oz.png";
import pastaBoxPresentation from "@/assets/products/pasta-box-presentation.png";
import smoothie250 from "@/assets/products/gobelet-smoothie-250ml.png";
import potSauce from "@/assets/products/pot-sauce-white.png";
import potDessert from "@/assets/products/pot-dessert-2.png";
import boiteAccompagnement from "@/assets/products/boite-accompagnement.png";
import sacBretellePetit from "@/assets/products/sac-bretelle-petit.png";
import sacPoubelle from "@/assets/products/sac-poubelle.png";
import bobineCB from "@/assets/products/bobine-thermique-cb.png";
import couvertsImage from "@/assets/products/couverts.png";
import pelleHotdog from "@/assets/products/pelle-hotdog-petite.png";

export interface ColorVariant {
  name: string;
  image: string;
  colorClass: string;
}

interface CategoryItem {
  name: string;
  link: string | null;
  image: string;
  count: string;
}

interface CategoryGroup {
  id: string;
  title: string;
  tagline: string;
  categories: CategoryItem[];
}

const categoryGroups: CategoryGroup[] = [
  {
    id: "contenants",
    title: "Contenants",
    tagline: "Bols, saladiers, barquettes et boîtes pour vos plats",
    categories: [
      { name: "Bols kraft", link: "/produits/bols-kraft", image: bolKraft, count: "5 tailles" },
      { name: "Saladiers carrés", link: "/produits/saladiers-carres", image: saladierCarre500, count: "4 tailles" },
      { name: "Barquettes bateau", link: "/produits/barquettes-bateau", image: barquetteBateauPresentation, count: "4 tailles" },
      { name: "Pots à soupe", link: "/produits/pots-soupe", image: potSoupe8oz, count: "4 tailles" },
      { name: "Pots à glace", link: "/produits/pots-glace", image: potGlace90, count: "4 tailles" },
      { name: "Pasta box", link: "/produits/pasta-box", image: pastaBoxPresentation, count: "3 tailles" },
      { name: "Pelles à hot dog", link: "/produits/pelle-hotdog", image: pelleHotdog, count: "3 tailles" },
      { name: "Boîte accompagnement", link: "/produits/boite-accompagnement", image: boiteAccompagnement, count: "2 modèles" },
      { name: "Pot à sauce", link: "/produits/pot-sauce", image: potSauce, count: "2 tailles" },
      { name: "Pot dessert", link: "/produits/pot-dessert", image: potDessert, count: "2 modèles" },
    ],
  },
  {
    id: "boissons",
    title: "Boissons",
    tagline: "Gobelets et pailles éco-responsables",
    categories: [
      { name: "Gobelets kraft", link: "/produits/gobelets-kraft", image: gobeletKraft110, count: "4 tailles" },
      { name: "Gobelets smoothie rPET", link: "/produits/gobelets-smoothie", image: smoothie250, count: "4 tailles" },
      { name: "Pailles papier", link: "/produits/pailles", image: pailleKraft, count: "6 modèles" },
    ],
  },
  {
    id: "emballages",
    title: "Emballages",
    tagline: "Sacs kraft et sacs à bretelles",
    categories: [
      { name: "Sacs kraft", link: "/produits/sacs-kraft", image: sacKraftPoignees, count: "3 modèles" },
      { name: "Sacs bretelle", link: "/produits/sac-bretelle", image: sacBretellePetit, count: "2 tailles" },
    ],
  },
  {
    id: "hygiene",
    title: "Hygiène",
    tagline: "Serviettes, gants, films et consommables",
    categories: [
      { name: "Serviettes", link: "/produits/serviettes", image: serviettesBlanches, count: "1 taille" },
      { name: "Bobines essuie-mains", link: "/produits/bobine-essuie-mains", image: bobineEssuieMains, count: "2 formats" },
      { name: "Gants", link: "/produits/gants", image: gantsNitrileBleus, count: "3 types" },
      { name: "Film étirable", link: "/produits/film-etirable", image: filmAlimentaire, count: "1 taille" },
      { name: "Film aluminium", link: "/produits/film-aluminium", image: filmAlu, count: "1 taille" },
      { name: "Couverts", link: "/produits/couverts", image: couvertsImage, count: "3 types" },
      { name: "Sac poubelle", link: "/produits/sac-poubelle", image: sacPoubelle, count: "130L" },
      { name: "Bobines thermiques", link: "/produits/bobine-thermique", image: bobineCB, count: "2 tailles" },
    ],
  },
  {
    id: "personnalisation",
    title: "Sur mesure",
    tagline: "Vos emballages à votre image",
    categories: [
      { name: "Bols personnalisés", link: "/produits/personnalisation", image: bolPersonnalise, count: "5 tailles" },
      { name: "Sacs personnalisés", link: "/produits/personnalisation", image: sacPersonnalise, count: "Sur mesure" },
      { name: "Gobelets personnalisés", link: "/produits/personnalisation", image: gobeletBlanc200, count: "4 tailles" },
      { name: "Serviettes personnalisées", link: "/produits/personnalisation", image: serviettesBlanches, count: "Sur mesure" },
    ],
  },
];

// Produits individuels pour le modal (ceux sans page dédiée)
const products = [
  {
    id: 10, name: "Bobine essuie-mains", category: "Bobines essuie-mains",
    description: "Bobine d'essuie-mains à dévidage central, douce et absorbante.",
    image: bobineEssuieMains, tags: ["Recyclable", "2 plis"], sizes: ["450 feuilles", "1000 feuilles"],
  },
  {
    id: 13, name: "Serviettes blanches", category: "Serviettes",
    description: "Serviettes en papier blanches, douces et absorbantes pour la restauration.",
    image: serviettesBlanches, tags: ["Recyclable", "2 plis"], sizes: ["30x30cm"],
  },
  {
    id: 14, name: "Film alimentaire étirable", category: "Film étirable",
    description: "Film alimentaire professionnel étirable pour conserver et protéger vos aliments.",
    image: filmAlimentaire, tags: ["Contact alimentaire", "Étirable"], sizes: ["30cm x 300m"],
  },
  {
    id: 19, name: "Film aluminium", category: "Film aluminium",
    description: "Rouleau de film aluminium professionnel pour la cuisson, la conservation et l'emballage alimentaire.",
    image: filmAlu, tags: ["Contact alimentaire", "Résistant chaleur"], sizes: ["30cm x 300m"],
  },
  {
    id: 15, name: "Bol kraft personnalisé", category: "Bols personnalisés",
    description: "Bol kraft personnalisable avec votre logo pour une identité de marque forte.",
    image: bolPersonnalise, tags: ["FSC", "Personnalisable", "Contact alimentaire"],
    sizes: ["500ml", "750ml", "1000ml", "1100ml", "1300ml"],
  },
  {
    id: 16, name: "Sac kraft personnalisé", category: "Sacs personnalisés",
    description: "Sac kraft avec impression de votre logo pour promouvoir votre établissement.",
    image: sacPersonnalise, tags: ["FSC", "Personnalisable", "Recyclable"], sizes: ["Sur mesure"],
  },
  {
    id: 17, name: "Gants nitrile", category: "Gants",
    description: "Gants en nitrile non poudrés, résistants aux produits chimiques et aux perforations.",
    image: gantsNitrileBleus, tags: ["Sans latex", "Non poudré", "Résistant"],
    sizes: ["S", "M", "L", "XL"],
    colors: [
      { name: "Bleu", image: gantsNitrileBleusSeul, colorClass: "bg-blue-500" },
      { name: "Noir", image: gantsNitrileNoirs, colorClass: "bg-gray-900" },
      { name: "Blanc", image: gantsVinyle, colorClass: "bg-white border border-gray-300" },
    ],
  },
  {
    id: 18, name: "Gants vinyle", category: "Gants",
    description: "Gants en vinyle économiques, idéaux pour la manipulation alimentaire.",
    image: gantsVinyle, tags: ["Sans latex", "Contact alimentaire"], sizes: ["S", "M", "L", "XL"],
  },
];

/* ─── Editorial Product Card ─── */
const ProductCard = ({
  category,
  index,
  onClick,
  size = "normal",
}: {
  category: CategoryItem;
  index: number;
  onClick: () => void;
  size?: "featured" | "normal";
}) => {
  const isFeatured = size === "featured";
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      onClick={onClick}
      className={`group relative cursor-pointer ${isFeatured ? "col-span-2 row-span-2" : ""}`}
    >
      {/* Image container */}
      <div
        className={`relative overflow-hidden rounded-2xl bg-background border border-border ${
          isFeatured ? "aspect-[4/5]" : "aspect-square"
        }`}
      >
        <img
          src={category.image}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-contain p-6 md:p-8 group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-accent/0 group-hover:bg-accent/5 transition-colors duration-500" />
        {/* Arrow */}
        <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
          <ArrowUpRight className="w-5 h-5 text-accent" />
        </div>
      </div>

      {/* Text */}
      <div className="mt-4 space-y-1">
        <h3
          className={`font-display font-semibold text-accent group-hover:text-primary transition-colors duration-300 ${
            isFeatured ? "text-2xl" : "text-lg"
          }`}
        >
          {category.name}
        </h3>
        <p className="text-sm text-muted-foreground">{category.count}</p>
      </div>
    </motion.article>
  );
};

/* ─── Main page ─── */
const Produits = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleCategoryClick = (category: CategoryItem) => {
    if (category.link) {
      navigate(category.link);
    } else {
      const product = products.find((p) => p.category === category.name);
      if (product) {
        setSelectedProduct(product);
        setIsModalOpen(true);
      }
    }
  };

  const filteredGroups = searchQuery
    ? categoryGroups
        .map((group) => ({
          ...group,
          categories: group.categories.filter((cat) =>
            cat.name.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((group) => group.categories.length > 0)
    : categoryGroups;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Catalogue Emballages Alimentaires Durables"
        description="Découvrez notre gamme complète d'emballages alimentaires écologiques : bols kraft, gobelets, sacs, barquettes. Certifiés FSC, livraison France."
      />
      <Header />
      <main id="main-content">
        {/* ─── HERO ─── */}
        <section className="pt-32 pb-20 md:pt-40 md:pb-28 relative overflow-hidden">
          {/* Decorative blobs */}
          <div className="absolute top-20 -right-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-0 -left-20 w-72 h-72 rounded-full bg-eco/5 blur-3xl" />

          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl"
            >
              <p className="text-sm font-medium tracking-[0.2em] uppercase text-primary mb-6">
                Catalogue
              </p>
              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-accent leading-[0.95] mb-8">
                Nos<br />
                Produits
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
                Emballages alimentaires durables, certifiés FSC et conformes aux normes européennes.
                Du sur-mesure à la livraison.
              </p>
            </motion.div>

            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-12 max-w-md"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un produit…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-base rounded-xl border-2 border-border focus:border-primary bg-card"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── CATEGORY GROUPS ─── */}
        {filteredGroups.map((group, groupIndex) => {
          const isEven = groupIndex % 2 === 0;
          const cats = group.categories;
          // First group: featured layout with first item large
          const hasFeatured = cats.length >= 4;

          return (
            <section
              key={group.id}
              className={`py-16 md:py-24 ${isEven ? "" : "bg-secondary/20"}`}
            >
              <div className="container mx-auto px-4">
                {/* Section header — editorial style */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16"
                >
                  <div>
                    <p className="text-sm font-medium tracking-[0.15em] uppercase text-primary mb-3">
                      {String(groupIndex + 1).padStart(2, "0")}
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl font-bold text-accent">
                      {group.title}
                    </h2>
                  </div>
                  <p className="text-muted-foreground text-lg md:max-w-sm md:text-right">
                    {group.tagline}
                  </p>
                </motion.div>

                {/* Grid — asymmetric for groups with 4+ items */}
                {hasFeatured ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 auto-rows-auto">
                    {/* Featured item spans 2 cols + 2 rows */}
                    <ProductCard
                      category={cats[0]}
                      index={0}
                      onClick={() => handleCategoryClick(cats[0])}
                      size="featured"
                    />
                    {/* Remaining items */}
                    {cats.slice(1).map((cat, i) => (
                      <ProductCard
                        key={cat.name}
                        category={cat}
                        index={i + 1}
                        onClick={() => handleCategoryClick(cat)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                    {cats.map((cat, i) => (
                      <ProductCard
                        key={cat.name}
                        category={cat}
                        index={i}
                        onClick={() => handleCategoryClick(cat)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          );
        })}

        {/* ─── CTA BANNER ─── */}
        <section className="py-24 md:py-32 bg-accent text-accent-foreground relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(215_90%_50%/0.15),transparent_60%)]" />
          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto text-center"
            >
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-6">
                Un projet sur mesure ?
              </h2>
              <p className="text-accent-foreground/70 text-lg mb-10 leading-relaxed">
                Contactez-nous pour un devis personnalisé. Impression, dimensions, conditionnement&nbsp;—
                tout est adaptable.
              </p>
              <Button
                size="lg"
                asChild
                className="bg-background text-accent hover:bg-background/90 rounded-full px-10 h-14 text-base font-medium"
              >
                <Link to="/devis">
                  Demander un devis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default Produits;
